import { useMemo } from "react";
import {
  buildDateStr,
  getDayOfWeek,
  getMonthDays,
  getToday,
  getWeekDates,
  formatDate,
} from "@/lib/dateManager";
import type { DayType, MarkedDay, Preferences } from "@/contexts/AppContext";

export interface GoalPeriodStats {
  workableDaysInPeriod: number;
  officeDaysInPeriod:   number;
  plannedDaysInPeriod:  number;
  /**
   * Office-equiv days whose reminder/alarm time has already passed while
   * still `unconfirmed` (past days, or today after the alarm). These do NOT
   * count toward the goal until the user confirms them — they are the "needs
   * confirmation" backlog surfaced by the bell.
   */
  unconfirmedDaysInPeriod: number;
  goalTarget:           number;
  /** 0–100 clamp (for bar width). */
  pctNumber:            number;
  /** Uncapped display value (one decimal). */
  pctDisplay:           string;
  isGoalMet:            boolean;
}

export interface UseGoalPeriodStatsParams {
  currentDate: Date;
  preferences: Preferences | null | undefined;
  markedDays:  MarkedDay[];
  /** Whether today's reminder/alarm time has passed (Planned → Unconfirmed trigger). */
  todayReminderPassed?: boolean;
  /** Imported holidays and other live holiday feeds that should not count as workable days. */
  holidaySet?: Set<string>;
  /** Day-type palette keyed by id; used to avoid hardcoded type-id semantics. */
  dayTypeMap?: Map<string, DayType>;
}

export interface UseGoalPeriodStatsResult {
  goalPeriodRange: string[];
  goalPeriodStats: GoalPeriodStats;
  goalPeriod:      "week" | "month" | "quarter";
  goalType:        "percent" | "days";
  goalValue:       number;
}

/**
 * Date strings of the goal period (per `preferences.goalPeriod`) that
 * contains `anchorDate`. Pure — the anchor decides WHICH week / month /
 * quarter, independent of any calendar view.
 */
export function buildGoalPeriodRange(
  anchorDate: Date,
  preferences: Preferences | null | undefined,
): string[] {
  const goalPeriod = preferences?.goalPeriod ?? "month";
  const y = anchorDate.getFullYear();
  const m = anchorDate.getMonth();
  if (goalPeriod === "week") return getWeekDates(anchorDate).map(formatDate);
  if (goalPeriod === "month") {
    const { daysInMonth } = getMonthDays(y, m);
    return Array.from({ length: daysInMonth }, (_, i) => buildDateStr(y, m, i + 1));
  }

  // Quarter periodicity with fiscal-year awareness (matches Analytics).
  const isFiscal         = preferences?.yearType === "fiscal";
  const fiscalStartMonth = (preferences?.fiscalStartMonth || 1) - 1;

  const fiscalBaseYear = (() => {
    if (!isFiscal) return y;
    return m < fiscalStartMonth ? y - 1 : y;
  })();

  const currentQuarterIndex = (() => {
    if (!isFiscal) return Math.floor(m / 3);
    let rel = m - fiscalStartMonth;
    if (rel < 0) rel += 12;
    return Math.floor(rel / 3);
  })();

  const baseMonth            = isFiscal ? fiscalStartMonth : 0;
  const startMonthOfQuarter  = (baseMonth + currentQuarterIndex * 3) % 12;

  return [0, 1, 2].flatMap((offset) => {
    const qm = (startMonthOfQuarter + offset) % 12;
    const qy = isFiscal && qm < fiscalStartMonth ? fiscalBaseYear + 1 : fiscalBaseYear;
    const { daysInMonth } = getMonthDays(qy, qm);
    return Array.from({ length: daysInMonth }, (_, i) => buildDateStr(qy, qm, i + 1));
  });
}

export interface ComputeGoalPeriodStatsParams {
  goalPeriodRange: string[];
  markedDays:      MarkedDay[];
  preferences:     Preferences | null | undefined;
  todayReminderPassed?: boolean;
  holidaySet?:     Set<string>;
  dayTypeMap?:     Map<string, DayType>;
}

/** Pure goal-stats computation over an explicit period range. */
export function computeGoalPeriodStats({
  goalPeriodRange,
  markedDays,
  preferences,
  todayReminderPassed = true,
  holidaySet,
  dayTypeMap,
}: ComputeGoalPeriodStatsParams): GoalPeriodStats {
  const goalType  = preferences?.goalType  ?? "percent";
  const goalValue = preferences?.goalValue ?? 50;

  const allMarkedMap = new Map<string, MarkedDay>();
  markedDays.forEach((d) => allMarkedMap.set(d.date, d));
  let workableDaysInPeriod = 0;
  let officeDaysInPeriod   = 0;
  let plannedDaysInPeriod  = 0;
  let unconfirmedDaysInPeriod = 0;
  const todayStr = getToday();

  for (const dateStr of goalPeriodRange) {
    const dow = getDayOfWeek(dateStr);
    if (!preferences?.includeWeekends && (dow === 0 || dow === 6)) continue;
    if (holidaySet?.has(dateStr)) continue;

    const dayData    = allMarkedMap.get(dateStr);
    const typeId     = dayData?.typeId;
    const status     = dayData?.status;
    const typeInfo   = typeId ? dayTypeMap?.get(typeId) : undefined;
    const baseCategory = typeInfo?.baseCategory;
    const isOfficeDay =
      baseCategory === "WORK_OFFICE" ||
      baseCategory === "WORK_EXTERNAL" ||
      typeId === "office" ||
      typeId === "client_visit" ||
      typeId === "business_travel";
    const isTimeOffOrHoliday =
      baseCategory === "ABSENCE" ||
      baseCategory === "SYSTEM_HOLIDAY";
    const isWorkDay =
      isOfficeDay ||
      baseCategory?.startsWith("WORK_") ||
      typeId === "remote";

    if (!typeId || (!isTimeOffOrHoliday && isWorkDay)) workableDaysInPeriod++;

    if (isOfficeDay) {
      if (status === "confirmed") {
        officeDaysInPeriod++;
      } else if (status === "unconfirmed") {
        // The reminder/alarm time is the trigger: a day is still "Planned"
        // (not yet due) while future or today-before-alarm; once its alarm
        // has fired it becomes "Unconfirmed" and stops counting until the
        // user validates it.
        const triggered =
          dateStr < todayStr ||
          (dateStr === todayStr && todayReminderPassed);
        if (triggered) unconfirmedDaysInPeriod++;
        else plannedDaysInPeriod++;
      }
    }
  }
  const goalTarget = goalType === "days"
    ? goalValue
    : Math.round(workableDaysInPeriod * (goalValue / 100));
  const uncappedPct = goalTarget > 0 ? (officeDaysInPeriod / goalTarget) * 100 : 0;
  const pctNumber  = Math.min(100, uncappedPct); // for the bar width
  const pctDisplay = uncappedPct.toFixed(1);
  const isGoalMet  = officeDaysInPeriod >= goalTarget && goalTarget > 0;
  return { workableDaysInPeriod, officeDaysInPeriod, plannedDaysInPeriod, unconfirmedDaysInPeriod, goalTarget, pctNumber, pctDisplay, isGoalMet };
}

/**
 * Computes the "Office Goal" card numbers for the current view.
 *
 * Input is always the unfiltered `markedDays` list — weekend filtering
 * is applied here according to `preferences.includeWeekends` so the
 * sidebar and Analytics views agree on what counts as a "workable
 * day" in the chosen period.
 *
 * Handles week / month / quarter periodicity, calendar or fiscal-year
 * anchoring, and both percentage + absolute-day goal semantics.
 */
export function useGoalPeriodStats({
  currentDate,
  preferences,
  markedDays,
  todayReminderPassed = true,
  holidaySet,
  dayTypeMap,
}: UseGoalPeriodStatsParams): UseGoalPeriodStatsResult {
  const goalPeriod = preferences?.goalPeriod ?? "month";
  const goalType   = preferences?.goalType   ?? "percent";
  const goalValue  = preferences?.goalValue  ?? 50;

  const goalPeriodRange = useMemo<string[]>(
    () => buildGoalPeriodRange(currentDate, preferences),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [goalPeriod, currentDate, preferences?.yearType, preferences?.fiscalStartMonth],
  );

  const goalPeriodStats = useMemo<GoalPeriodStats>(
    () => computeGoalPeriodStats({
      goalPeriodRange,
      markedDays,
      preferences,
      todayReminderPassed,
      holidaySet,
      dayTypeMap,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      goalPeriodRange,
      markedDays,
      preferences?.includeWeekends,
      goalType,
      goalValue,
      todayReminderPassed,
      holidaySet,
      dayTypeMap,
    ],
  );

  return { goalPeriodRange, goalPeriodStats, goalPeriod, goalType, goalValue };
}
